import { LinearProgress } from "@material-ui/core";
import * as React from "react";
import { useGetMeQuery } from "../../../api/rtk/MeApi";
import { useGetExamSessionsAvailableForSignUpQuery } from "../../../api/rtk/SignUpApi";
import { Switzerland, UrlQueryParameters } from "../../../data/constants";
import {
  CertificationDataContextModel,
  CertificationDataWizardContextModel,
  ModuleReference,
} from "../../../model/CertificationDataContextModel";
import { CourseForSignupViewModel } from "../../../model/ExamSignup/ExamSessionForSignupViewModel";
import { initialCandidate } from "../../candidate/CandidateConstants";
import { getCart } from "../../../api/CartApi";
import StartCertificationSteps from "./StartCertificationSteps";
import { useQuery } from "../../../utils/UseQuery";
import { CartViewModel } from "../../../model/Cart/CartViewModel";

const initialContextData: CertificationDataWizardContextModel = {
  data: {} as CertificationDataContextModel,
  availableExamSessions: [],
  referenceCourses: [],
  referenceModules: [],
  setValues: () => {},
  activeStep: StartCertificationSteps.PersonalDataStep,
  handleNextStep: () => {},
  handlePrevStep: () => {},
  setActiveStep: (step: number) => {},
  refetch: () => {},
  isLoading: false,
};

const initialFormData: CertificationDataContextModel = {
  candidate: initialCandidate,
  modulesSelection: [],
};

const initialCourses: CourseForSignupViewModel[] = [];
const initialModules: ModuleReference[] = [];

const CertificationDataWizardContext = React.createContext<CertificationDataWizardContextModel>(initialContextData);

export const CertificationDataWizardProvider: React.FC = ({ children }) => {
  const [data, setData] = React.useState<CertificationDataContextModel>(initialFormData);
  const [referenceCourses, setReferenceCourses] = React.useState<CourseForSignupViewModel[]>(initialCourses);
  const [referenceModules, setReferenceModules] = React.useState<ModuleReference[]>(initialModules);
  const [activeStep, setActiveStep] = React.useState(0);
  const [isFetchingCart, setIsFetchingCart] = React.useState(false);

  const { data: fetchedMe, isFetching: isMeFetching } = useGetMeQuery();
  const {
    data: availableExamSessions,
    isFetching: isAvailableExamSessionsLoading,
    refetch,
  } = useGetExamSessionsAvailableForSignUpQuery(
    undefined,
    { skip: isMeFetching || (activeStep !== StartCertificationSteps.PersonalDataStep && activeStep !== StartCertificationSteps.ExamLocationAndDateStep)}
  );
  const datatransTrxId = useQuery().get(UrlQueryParameters.datatransTrxId);

  const [currentCart, setCurrentCart] = React.useState<CartViewModel | null>(null);

  const isLoading = isAvailableExamSessionsLoading || isMeFetching;

  // We need to pass to the form already loaded data, otherwise the form will be initialized with initial empty data.
  // This expression checks whether initial values were updated with the loaded ones.
  const areInitialValuesUpdated =
    referenceCourses !== initialCourses && referenceModules !== initialModules && currentCart !== null;

  const step = useQuery().get("step");

  React.useEffect(() => {
    if (activeStep === StartCertificationSteps.PersonalDataStep || activeStep === StartCertificationSteps.ExamLocationAndDateStep){
       refetch();
    }
  }, [activeStep, refetch]);

  React.useEffect(() => {
    if (fetchedMe !== undefined) {
      setData((prev) => ({
        ...prev,
        candidate: {
          ...fetchedMe,
          homeCountry: fetchedMe.nationalityIsSwiss ? Switzerland : fetchedMe.placeOfOrigin,
          employerCountry: fetchedMe.employerCountry || Switzerland,
          preferredContact: fetchedMe.privateIsPreferredContact ? "private" : fetchedMe.preferredContact,
        },
      }));
    }
  }, [fetchedMe]);

  React.useEffect(() => {
    let unmounted = false;

    if (currentCart === null && referenceModules.length > 0 && referenceCourses.length > 0) {
      setIsFetchingCart(true);
      getCart(!datatransTrxId).then((cart) => {
        if (!unmounted) {
          setCurrentCart(cart);
          const availableModules =
            cart?.modules!.filter(
              (m) =>
                referenceModules.find((rm) => rm.id === m.moduleId) &&
                referenceCourses.find((rc) => rc.id === m.courseId)
            ) ?? [];
          if (availableModules.length > 0 && !unmounted) {
            setData((prev) => ({ ...prev, modulesSelection: availableModules }));
            if (!unmounted && (step === "confirmation" || activeStep === StartCertificationSteps.PersonalDataStep)) {
              setActiveStep(StartCertificationSteps.PaymentAndCompletionStep);
            }
          }
        }
      }).finally(() => {
        if (!unmounted) {
          setIsFetchingCart(false);
        }
      });
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceModules, referenceCourses]);

  React.useEffect(() => {
    setData((prev) => ({ ...prev, modulesSelection: [] }));
  }, [data.candidate.correspondenceLanguage]);

  React.useEffect(() => {
    if (availableExamSessions === undefined) {
      return;
    }
    const referenceCourses: CourseForSignupViewModel[] = [];
    const referenceModules: ModuleReference[] = [];

    availableExamSessions.forEach((examSession) => {
      examSession.courses.forEach((course) => {
        if (referenceCourses.find((c) => c.id === course.id) === undefined) {
          referenceCourses.push(course);
        }
        course.modules.forEach((module) => {
          referenceModules.push({ ...module, examSessionId: examSession.id });
        });
      });
    });

    setReferenceCourses(referenceCourses);
    setReferenceModules(referenceModules);
  }, [availableExamSessions]);

  const handleNextStep = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handlePrevStep = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const setValues = (values: Partial<CertificationDataContextModel>): void => {
    setData(
      (prevData) =>
        ({
          ...prevData,
          ...values,
        } as CertificationDataContextModel)
    );
  };

  if ((isLoading || !areInitialValuesUpdated) && activeStep === StartCertificationSteps.PersonalDataStep) {
    return <LinearProgress variant="query" />;
  }

  return (
    <CertificationDataWizardContext.Provider
      value={{
        data,
        availableExamSessions: availableExamSessions ?? [],
        referenceCourses,
        referenceModules,
        setValues,
        activeStep,
        handleNextStep,
        handlePrevStep,
        setActiveStep,
        refetch,
        isLoading: isLoading || isFetchingCart,
      }}
    >
      {children}
    </CertificationDataWizardContext.Provider>
  );
};

export const useSignUpContext = () => React.useContext(CertificationDataWizardContext);
